// M61 (TH 5): licencias de conducción del trabajador (tipo, vencimiento y verificación). Sin número ni archivo (LOPDP).
// Quién verifica y cuándo lo pone la base. Aviso si vence en 30 días o menos (sugerido por StorSafe, no normativo).
import React, { useCallback, useEffect, useState } from 'react';
import { fechaEc, hoyEc } from '../../lib/fechas';
import {
  agregarLicencia, avisoVencimiento, borrarLicencia, cambiarVencimiento, DESCRIPCION_LICENCIA, leerLicencias, leerPuestoLicencias, TIPOS_LICENCIA, verificarLicencia, vigente,
  type FilaLicencia, type TipoLicencia,
} from '../../lib/licencias-conduccion';

interface Props { companyId: string; workerId: string | null; positionId: string | null; puedeEditar: boolean; usuarioId: string | null }

const caja: React.CSSProperties = { background: 'rgba(56,189,248,0.06)', border: '1px solid rgba(56,189,248,0.3)', borderRadius: 10, padding: 12,
  display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 };
const nota: React.CSSProperties = { color: '#94a3b8', fontSize: '0.78rem', margin: 0, lineHeight: 1.45 };
const control: React.CSSProperties = { background: '#0f172a', color: '#e2e8f0', border: '1px solid #334155', borderRadius: 8, padding: '8px 10px',
  minHeight: 40, fontSize: '0.86rem', maxWidth: '100%', minWidth: 0 };
const boton: React.CSSProperties = { ...control, cursor: 'pointer', background: '#1e293b' };

export default function LicenciasConduccionSeccion({ companyId, workerId, positionId, puedeEditar, usuarioId }: Props) {
  const [filas, setFilas] = useState<FilaLicencia[]>([]);
  const [disponible, setDisponible] = useState(true);
  const [puesto, setPuesto] = useState<{ opera: boolean; aceptadas: string[] } | null>(null);
  const [tipo, setTipo] = useState<TipoLicencia>('B');
  const [vence, setVence] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState('');
  const hoy = hoyEc();

  const cargar = useCallback(async () => {
    if (!workerId) return;
    const r = await leerLicencias(companyId, workerId);
    setFilas(r.filas); setDisponible(r.disponible);
  }, [companyId, workerId]);
  useEffect(() => { cargar(); }, [cargar]);
  useEffect(() => {
    if (!positionId) { setPuesto(null); return; }
    let vivo = true;
    leerPuestoLicencias(positionId).then(p => { if (vivo) setPuesto(p); });
    return () => { vivo = false; };
  }, [positionId]);

  const titulo = <div style={{ color: '#7dd3fc', fontWeight: 700, fontSize: '0.9rem' }}>Licencias de conducción</div>;
  if (!workerId) return <div style={caja}>{titulo}<p style={nota}>Guarda primero el trabajador; después podrás registrar sus licencias.</p></div>;
  if (!disponible) return <div style={caja}>{titulo}<p style={nota}>Esta función todavía no está disponible para tu empresa.</p></div>;

  const hacer = async (f: () => Promise<void>) => {
    setOcupado(true); setError('');
    try { await f(); await cargar(); } catch (x) { setError(x instanceof Error ? x.message : String(x)); } finally { setOcupado(false); }
  };
  const agregar = () => {
    if (!vence) { setError('Escribe la fecha de vencimiento.'); return; }
    hacer(async () => { await agregarLicencia(companyId, workerId, tipo, vence); setVence(''); });
  };
  const sirve = (l: FilaLicencia) => !!puesto?.aceptadas.includes(l.tipo) && vigente(l, hoy);

  return (
    <div style={caja} aria-label="Licencias de conducción">
      {titulo}
      {puesto?.opera ? (
        <p style={nota}>Su puesto opera vehículos o maquinaria y acepta: <b style={{ color: '#e2e8f0' }}>{puesto.aceptadas.map(t => `tipo ${t}`).join(', ')}</b>.
          {' '}{filas.some(sirve) ? 'Tiene una licencia vigente que sirve para el puesto.' : 'Falta una licencia vigente de un tipo aceptado (TH 5 queda En proceso).'}</p>
      ) : (
        <p style={nota}>Su puesto no está marcado como "Opera vehículos o maquinaria": TH 5 no le pide licencia.</p>
      )}
      {filas.length === 0 ? <p style={nota}>Sin licencias registradas.</p> : (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {filas.map(l => {
            const aviso = avisoVencimiento(l, hoy);
            return (
              <li key={l.id} style={{ border: '1px solid #334155', borderRadius: 8, padding: 8, display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
                <div style={{ color: '#e2e8f0', fontSize: '0.86rem', overflowWrap: 'anywhere' }}>
                  <b>Tipo {l.tipo}</b> · {DESCRIPCION_LICENCIA[l.tipo as TipoLicencia] ?? ''}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                  <label style={{ ...nota, display: 'flex', gap: 6, alignItems: 'center' }}>Vence
                    <input type="date" style={control} value={l.fecha_vencimiento} disabled={!puedeEditar || ocupado} aria-label={`Vencimiento tipo ${l.tipo}`}
                      onChange={e => e.target.value && hacer(() => cambiarVencimiento(l.id, e.target.value))} />
                  </label>
                  <span style={nota}>{l.verificada_at ? `Verificada el ${fechaEc(l.verificada_at)}` : 'Sin verificar'}</span>
                  {puedeEditar && !l.verificada_at && usuarioId && (
                    <button type="button" style={boton} disabled={ocupado} onClick={() => hacer(() => verificarLicencia(l.id, usuarioId))}>Verificar</button>
                  )}
                  {puedeEditar && <button type="button" style={boton} disabled={ocupado} aria-label={`Borrar licencia tipo ${l.tipo}`} onClick={() => hacer(() => borrarLicencia(l.id))}>Borrar</button>}
                </div>
                {aviso && <p role="status" style={{ ...nota, color: vigente(l, hoy) ? '#fbbf24' : '#f87171' }}>⚠ {aviso}</p>}
              </li>
            );
          })}
        </ul>
      )}
      {puedeEditar && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'flex-end' }}>
          <label style={{ ...nota, display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0, maxWidth: '100%' }}>Tipo
            <select style={control} value={tipo} disabled={ocupado} onChange={e => setTipo(e.target.value as TipoLicencia)} aria-label="Tipo de licencia">
              {TIPOS_LICENCIA.map(t => <option key={t} value={t}>{`Tipo ${t} · ${DESCRIPCION_LICENCIA[t]}`}</option>)}
            </select>
          </label>
          <label style={{ ...nota, display: 'flex', flexDirection: 'column', gap: 4 }}>Vence
            <input type="date" style={control} value={vence} disabled={ocupado} onChange={e => setVence(e.target.value)} aria-label="Fecha de vencimiento" />
          </label>
          <button type="button" style={boton} disabled={ocupado} onClick={agregar}>Agregar licencia</button>
        </div>
      )}
      {error && <p role="alert" style={{ ...nota, color: '#f87171' }}>{error}</p>}
      <p style={nota}>Solo el tipo y el vencimiento: no se guarda el número de la licencia ni su imagen (LOPDP). Quién verifica y cuándo lo registra el sistema.</p>
    </div>
  );
}
